'use strict';

/* global globalThis */

// Provider-neutral primitives shared by the GCS and S3 upload state machines.
// The backend remains authoritative for lifecycle and completion. Signed URLs
// and session URIs are bearer authority: pass them to providers, never log them.
// This module deliberately knows nothing about either provider's request shape.

const BROWSER_UPLOAD_STRATEGIES = Object.freeze({
  GCS_RESUMABLE: 'GCS_RESUMABLE',
  S3_SINGLE_PART: 'S3_SINGLE_PART',
  S3_MULTIPART: 'S3_MULTIPART',
});

const AUTHORIZATION_ERROR_CODES = new Set([
  'AccessDenied',
  'AuthorizationQueryParametersError',
  'ExpiredToken',
  'InvalidAccessKeyId',
  'InvalidToken',
  'RequestExpired',
  'SignatureDoesNotMatch',
]);

class UploadHttpError extends Error {
  constructor(message, { status, providerCode, retriable, authorization }) {
    super(message);
    this.name = 'UploadHttpError';
    this.status = status;
    this.providerCode = providerCode;
    this.retriable = retriable;
    this.authorization = authorization;
  }
}

// Internal control-flow signal: multipart bookkeeping from an old provider
// session must be discarded instead of being retried against a replacement.
class UploadSessionRestartError extends Error {
  constructor() {
    super('The provider replaced the multipart upload session.');
    this.name = 'UploadSessionRestartError';
  }
}

function defaultFetchImpl() {
  const owner = typeof window === 'undefined' ? globalThis : window;
  // Some browsers require fetch to retain its Window receiver.
  return owner.fetch.bind(owner);
}

function isAbortError(error) {
  return Boolean(error && error.name === 'AbortError');
}

function throwIfAborted(signal) {
  if (signal?.aborted) {
    throw new DOMException('Upload aborted', 'AbortError');
  }
}

function providerErrorCode(body) {
  // S3 commonly returns XML while GCS/emulators may return JSON.
  const xmlCode = /<Code>([^<]+)<\/Code>/.exec(body || '');
  if (xmlCode) return xmlCode[1];
  try {
    const parsed = JSON.parse(body || '{}');
    return parsed.error?.code || parsed.code || null;
  } catch {
    return null;
  }
}

async function responseError(response, operation) {
  let body = '';
  try {
    // Bound the diagnostic body so provider errors cannot flood UI state.
    body = (await response.text()).slice(0, 2_000);
  } catch {
    // A provider response body is optional and may be hidden by CORS.
  }
  const code = providerErrorCode(body);
  const authorization =
    response.status === 401 ||
    response.status === 403 ||
    AUTHORIZATION_ERROR_CODES.has(code);
  const retriable =
    response.status === 408 ||
    response.status === 429 ||
    response.status >= 500;
  const suffix = code ? ` (${code})` : '';
  return new UploadHttpError(
    `${operation} failed with HTTP ${response.status}${suffix}`,
    {
      status: response.status,
      providerCode: code,
      retriable,
      authorization,
    }
  );
}

function wait(milliseconds, signal) {
  if (!milliseconds) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(resolve, milliseconds);
    if (!signal) return;
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timeout);
        reject(new DOMException('Upload aborted', 'AbortError'));
      },
      { once: true }
    );
  });
}

async function fetchWithRetry(
  makeRequest,
  { operation, accepted, refreshAuthority, maxRetries, retryDelayMs, signal }
) {
  let retries = 0;

  // Authorization failures first ask the backend for fresh authority. Network
  // and transient provider failures use the same bounded exponential backoff.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    try {
      const response = await makeRequest();
      if (accepted(response)) return response;
      const error = await responseError(response, operation);
      if (error.authorization && refreshAuthority && retries < maxRetries) {
        retries += 1;
        await refreshAuthority();
        continue;
      }
      if (!error.retriable || retries >= maxRetries) throw error;
      retries += 1;
    } catch (error) {
      if (isAbortError(error)) throw error;
      if (error instanceof UploadSessionRestartError) throw error;
      if (error instanceof UploadHttpError && !error.retriable) throw error;
      if (retries >= maxRetries) throw error;
      retries += 1;
    }
    await wait(retryDelayMs * 2 ** (retries - 1), signal);
  }
}

function progressPercent(uploaded, total) {
  if (total === 0) return 99;
  // 100 is reserved for the backend completion mutation, not byte transfer.
  return Math.min(99, Math.round((uploaded / total) * 100));
}

async function reportProgress(upload, api, onProgress, uploaded, done, signal) {
  throwIfAborted(signal);
  onProgress({ uploaded, total: upload.file.size, done });
  if (done) return;
  try {
    await api.update({
      id: upload.record.id,
      sessionUri: upload.record.sessionUri || undefined,
      progress: progressPercent(uploaded, upload.file.size),
      status: 'RUNNING',
    });
  } catch (error) {
    // Cancellation may race the progress mutation. Prefer the cancellation
    // signal so callers do not incorrectly mark the upload STALE.
    throwIfAborted(signal);
    throw error;
  }
  throwIfAborted(signal);
}

function applyUploadAuthorization(upload, authorization) {
  const previousSession = upload.record.sessionUri;

  // The mutation may return a full upload plus convenience fields. Apply both
  // so the in-memory record matches the backend before the next request.
  if (authorization.upload) {
    Object.assign(upload.record, authorization.upload);
  }
  for (const field of [
    'cloudProvider',
    'uploadStrategy',
    'signedUrl',
    'sessionUri',
    'partSize',
  ]) {
    if (Object.prototype.hasOwnProperty.call(authorization, field)) {
      upload.record[field] = authorization[field];
    }
  }

  return Boolean(
    previousSession &&
      upload.record.sessionUri &&
      previousSession !== upload.record.sessionUri
  );
}

function uploadErrorMessage(error) {
  if (isAbortError(error)) return 'Upload canceled.';
  if (error instanceof UploadHttpError) {
    if (error.authorization) {
      return 'Upload authorization was rejected after refresh. Retry the upload; if it persists, check access and system time.';
    }
    if (error.status === 409 || error.status === 412) {
      return 'The destination changed while this upload was running. Choose a new file name or refresh the data page.';
    }
    if (error.retriable) {
      return 'The storage provider remained unavailable after retries. Resume the upload when connectivity recovers.';
    }
    if (error.status === 404 || error.status === 410) {
      return 'The resumable upload session expired. Resume the upload to create a new session.';
    }
  }
  return error instanceof Error
    ? error.message
    : 'The upload failed unexpectedly.';
}

module.exports = {
  BROWSER_UPLOAD_STRATEGIES,
  UploadHttpError,
  UploadSessionRestartError,
  applyUploadAuthorization,
  defaultFetchImpl,
  fetchWithRetry,
  isAbortError,
  reportProgress,
  responseError,
  throwIfAborted,
  uploadErrorMessage,
};
